import { ScannerProvider, ScannerConfig, ScanResult } from '../types'
import { captureException } from 'lib/monitoring'
import ScanbotSDK from 'scanbot-web-sdk'
import { BarcodeScannerViewConfiguration } from 'scanbot-web-sdk/@types/model/configuration/barcode-scanner-view-configuration'
import { IBarcodeScannerHandle } from 'scanbot-web-sdk/@types/interfaces/i-barcode-scanner-handle'
import {
  isGS1UDI,
  parseGS1Barcode,
  formatGS1Barcode,
  isDeviceIdOnlyBarcode,
  isMissingEssentialIdentifiers,
} from '../../utils/scanner/gs1Utils'

declare global {
  interface Window {
    scanbotSDK: any
    ScanbotSDK: typeof ScanbotSDK
  }
}

export class ScanbotScannerProvider implements ScannerProvider {
  private scanner: ScanbotSDK | null = null
  private isInitialized: boolean = false
  private isActive: boolean = false
  private barcodeScanner: IBarcodeScannerHandle | null = null
  private nonGS1Timer: NodeJS.Timeout | null = null
  private lastNonGS1Barcode: { text: string; format: string } | null = null
  private currentDeviceId: string | undefined = undefined
  private currentConfig: ScannerConfig | null = null

  // New state for handling split barcodes
  private splitBarcodeTimer: NodeJS.Timeout | null = null
  private collectedBarcodes: Array<{
    text: string
    format: string
    rawBarcode: any
    timestamp: number
  }> = []
  private isCollectingMultipleBarcodes: boolean = false
  private readonly SPLIT_BARCODE_TIMEOUT = 5000 // 5 seconds as set by user

  constructor() {
    this.scanner = null
  }

  async init(): Promise<void> {
    try {
      if (!this.isInitialized) {
        // Wait for Scanbot SDK to load
        if (!window.ScanbotSDK) {
          await new Promise<void>((resolve) => {
            const checkSDK = () => {
              if (window.ScanbotSDK) {
                resolve()
              } else {
                setTimeout(checkSDK, 100)
              }
            }
            checkSDK()
          })
        }

        const licenseKey = process.env.REACT_APP_SCANBOT_LICENSE_KEY

        if (!licenseKey) {
          throw new Error('Scanbot license key is not set')
        }

        this.scanner = await window.ScanbotSDK.initialize({
          licenseKey,
          enginePath:
            'https://cdn.jsdelivr.net/npm/scanbot-web-sdk@7.1.0/bundle/bin/barcode-scanner/',
          requestSuffix: '?v=7.1.0',
        })

        // Check license validity
        const licenseInfo = await this.scanner.getLicenseInfo()
        if (!licenseInfo.isValid()) {
          throw new Error('Invalid Scanbot SDK license')
        }

        this.isInitialized = true
      }
    } catch (error) {
      captureException(error as Error)
      throw error
    }
  }

  async start(config: ScannerConfig): Promise<void> {
    try {
      if (!this.isInitialized || !this.scanner) {
        throw new Error('Scanner not initialized')
      }

      if (this.isActive) {
        return
      }

      this.currentConfig = config

      // Create and configure the barcode scanner
      const barcodeConfig: BarcodeScannerViewConfiguration = {
        containerId: 'scanbot-video-container',
        finder: {
          visible: false,
        },
        userGuidance: {
          visible: false,
        },
        zoom: 1.5,
        desiredRecognitionResolution: 1080,
        preferredCamera: this.currentDeviceId,
        onBarcodesDetected: (result: { barcodes: any[] }) => {
          if (result.barcodes.length === 0) return

          // Find the first barcode that is a UDI
          let gs1Barcode = null
          let nonGS1Barcode = null

          for (const b of result.barcodes) {
            if (isGS1UDI(b.text)) {
              gs1Barcode = b
              break
            } else if (!nonGS1Barcode) {
              nonGS1Barcode = b
            }
          }

          // Handle split barcode collection mode
          if (this.isCollectingMultipleBarcodes) {
            if (gs1Barcode) {
              // Add this barcode to our collection if it's not a duplicate
              this.addToCollection(gs1Barcode)
            }
            // Don't process individual barcodes while collecting - wait for timer
            return
          }

          // Check if we found a device ID-only barcode to start collection mode
          if (gs1Barcode && isDeviceIdOnlyBarcode(gs1Barcode.text)) {
            // Clear any existing non-GS1 timer since we're starting split barcode collection
            if (this.nonGS1Timer) {
              clearTimeout(this.nonGS1Timer)
              this.nonGS1Timer = null
            }
            this.lastNonGS1Barcode = null

            // Start collecting multiple barcodes
            this.startSplitBarcodeCollection(gs1Barcode, config)
            return
          }

          // Check if we found a potentially split barcode (missing device ID but has other identifiers)
          if (gs1Barcode && isMissingEssentialIdentifiers(gs1Barcode.text)) {
            // Clear any existing non-GS1 timer
            if (this.nonGS1Timer) {
              clearTimeout(this.nonGS1Timer)
              this.nonGS1Timer = null
            }
            this.lastNonGS1Barcode = null

            // Start collecting multiple barcodes with this non-device-ID barcode
            this.startSplitBarcodeCollection(gs1Barcode, config)
            return
          }

          // If we found a complete GS1 barcode (not device ID-only and has essential identifiers), process normally
          if (gs1Barcode) {
            if (this.nonGS1Timer) {
              clearTimeout(this.nonGS1Timer)
              this.nonGS1Timer = null
            }
            this.lastNonGS1Barcode = null

            let allIdentifiers: { ai: string; value: string }[] = []

            // Process GS1 barcode as before...
            if (gs1Barcode.extractedDocument?.children) {
              for (const element of gs1Barcode.extractedDocument.children) {
                const ai = element.fields.find(
                  (f: { type: { name: string } }) =>
                    f.type.name === 'ApplicationIdentifier'
                )?.value.text
                const value = element.fields.find(
                  (f: { type: { name: string } }) => f.type.name === 'RawValue'
                )?.value.text

                if (ai && value) {
                  allIdentifiers.push({ ai, value })
                }
              }

              // If we have all identifiers from extractedDocument, construct the barcode
              if (allIdentifiers.length > 0) {
                // Use shared formatting function for consistency
                const rawBarcodeData = formatGS1Barcode(allIdentifiers)

                const scanResult: ScanResult = {
                  barcodeData: rawBarcodeData,
                  symbologyName: gs1Barcode.format,
                }

                config.onScan(scanResult)
                return
              }
            }

            // Fall back to raw barcode parsing if extractedDocument is not available
            let rawBarcodeData = gs1Barcode.text

            // Parse the barcode and format it with parentheses
            const identifiers = parseGS1Barcode(rawBarcodeData)
            const formattedBarcode = formatGS1Barcode(identifiers)

            const scanResult: ScanResult = {
              barcodeData: formattedBarcode || rawBarcodeData, // Fallback to original if parsing fails
              symbologyName: gs1Barcode.format,
            }

            config.onScan(scanResult)
            return
          }

          // If we found a non-GS1 barcode and don't have a pending timer
          if (nonGS1Barcode && !this.nonGS1Timer) {
            this.lastNonGS1Barcode = nonGS1Barcode

            // Set a 3-second timer to process the non-GS1 barcode if no GS1 is found
            this.nonGS1Timer = setTimeout(() => {
              if (this.lastNonGS1Barcode) {
                const scanResult: ScanResult = {
                  barcodeData: this.lastNonGS1Barcode.text,
                  symbologyName: this.lastNonGS1Barcode.format,
                }
                config.onScan(scanResult)
                this.lastNonGS1Barcode = null
              }
              this.nonGS1Timer = null
            }, 3000)
          }
        },
        onError: (error: Error) => {
          if (config.onError) {
            config.onError(error)
          }
        },
      }

      this.barcodeScanner = await this.scanner.createBarcodeScanner(
        barcodeConfig
      )

      // The scanner starts automatically when created
      this.isActive = true
    } catch (error) {
      captureException(error as Error)
      throw error
    }
  }

  async stop(): Promise<void> {
    try {
      if (this.nonGS1Timer) {
        clearTimeout(this.nonGS1Timer)
        this.nonGS1Timer = null
      }
      this.lastNonGS1Barcode = null

      // Clean up split barcode collection state
      this.resetSplitBarcodeState()

      if (this.barcodeScanner) {
        await this.barcodeScanner.dispose()
        this.barcodeScanner = null
      }
      this.isActive = false
    } catch (error) {
      captureException(error as Error)
      throw error
    }
  }

  async changeCamera(deviceId: string): Promise<void> {
    try {
      this.currentDeviceId = deviceId

      // Reset split barcode collection state when changing cameras
      this.resetSplitBarcodeState()

      // If scanner is active, we need to restart with the new camera
      if (this.isActive && this.currentConfig) {
        await this.stop()
        await this.start(this.currentConfig)
        this.barcodeScanner?.switchCamera(deviceId)
      }
    } catch (error) {
      captureException(error as Error)
      throw error
    }
  }

  isScannerActive(): boolean {
    return this.isActive
  }

  startDecoding(): void {
    // The scanner starts automatically when created
    this.isActive = true
  }

  stopDecoding(): void {
    // Clean up split barcode collection state
    this.resetSplitBarcodeState()

    if (this.barcodeScanner) {
      this.barcodeScanner.dispose()
      this.barcodeScanner = null
    }
    this.isActive = false
  }

  /**
   * Starts the collection mode for split barcodes
   */
  private startSplitBarcodeCollection(
    initialBarcode: any,
    config: ScannerConfig
  ): void {
    this.isCollectingMultipleBarcodes = true
    this.collectedBarcodes = [
      {
        text: initialBarcode.text,
        format: initialBarcode.format,
        rawBarcode: initialBarcode,
        timestamp: Date.now(),
      },
    ]

    // Set a 8-second timer to process collected barcodes
    this.splitBarcodeTimer = setTimeout(() => {
      this.processCollectedBarcodes(config)
    }, this.SPLIT_BARCODE_TIMEOUT)
  }

  /**
   * Adds a barcode to the collection if we're in collection mode
   */
  private addToCollection(barcode: any): boolean {
    if (!this.isCollectingMultipleBarcodes) return false

    // Check if this barcode is already in our collection
    const isDuplicate = this.collectedBarcodes.some(
      (collected) => collected.text === barcode.text
    )

    if (!isDuplicate) {
      this.collectedBarcodes.push({
        text: barcode.text,
        format: barcode.format,
        rawBarcode: barcode,
        timestamp: Date.now(),
      })
    }

    return true
  }

  /**
   * Resets the split barcode collection state
   */
  private resetSplitBarcodeState(): void {
    if (this.splitBarcodeTimer) {
      clearTimeout(this.splitBarcodeTimer)
      this.splitBarcodeTimer = null
    }
    this.collectedBarcodes = []
    this.isCollectingMultipleBarcodes = false
  }

  /**
   * Processes a collected set of barcodes and triggers the onScan callback
   */
  private processCollectedBarcodes(config: ScannerConfig): void {
    if (this.collectedBarcodes.length === 0) return

    const combinedBarcodeData = this.combineBarcodes(this.collectedBarcodes)

    // Use the format from the first barcode (they should all be similar formats)
    const symbologyName = this.collectedBarcodes[0].format

    const scanResult: ScanResult = {
      barcodeData: combinedBarcodeData,
      symbologyName,
    }

    config.onScan(scanResult)
    this.resetSplitBarcodeState()
  }

  /**
   * Combines multiple barcode texts into a single GS1 formatted string
   */
  private combineBarcodes(
    barcodes: Array<{ text: string; format: string; rawBarcode?: any }>
  ): string {
    const allBarcodeIdentifiers: Array<{ ai: string; value: string }[]> = []

    // Parse each barcode individually using the same logic as single barcode processing
    for (const barcode of barcodes) {
      if (isGS1UDI(barcode.text)) {
        // Use the raw barcode object if available for proper parsing
        const barcodeObject = barcode.rawBarcode || {
          text: barcode.text,
          format: barcode.format,
        }
        const identifiers = this.parseIndividualBarcode(barcodeObject)
        if (identifiers.length > 0) {
          allBarcodeIdentifiers.push(identifiers)
        }
      }
    }

    return this.combineBarcodesFromIdentifiers(allBarcodeIdentifiers)
  }

  /**
   * Parses a single barcode using the existing parsing logic
   * This method extracts identifiers from either extractedDocument or raw text
   */
  private parseIndividualBarcode(
    barcode: any
  ): { ai: string; value: string }[] {
    let identifiers: { ai: string; value: string }[] = []

    // First try to get identifiers from extractedDocument if available
    if (barcode.extractedDocument?.children) {
      for (const element of barcode.extractedDocument.children) {
        const ai = element.fields.find(
          (f: { type: { name: string } }) =>
            f.type.name === 'ApplicationIdentifier'
        )?.value.text
        const value = element.fields.find(
          (f: { type: { name: string } }) => f.type.name === 'RawValue'
        )?.value.text

        if (ai && value) {
          identifiers.push({ ai, value })
        }
      }
    }

    // If we didn't get identifiers from extractedDocument, fall back to raw text parsing
    if (identifiers.length === 0) {
      identifiers = parseGS1Barcode(barcode.text)
    }

    return identifiers
  }

  /**
   * Combines multiple parsed barcode identifiers into a single GS1 formatted string
   * Each barcode should already be parsed into its individual identifiers
   */
  private combineBarcodesFromIdentifiers(
    barcodeIdentifiers: Array<{ ai: string; value: string }[]>
  ): string {
    const allIdentifiers: { ai: string; value: string }[] = []

    // Collect all identifiers from all barcodes
    for (const identifiers of barcodeIdentifiers) {
      allIdentifiers.push(...identifiers)
    }

    // Remove duplicates based on AI (Application Identifier)
    // Keep the first occurrence of each AI
    const uniqueIdentifiers = allIdentifiers.filter(
      (identifier, index, self) =>
        index === self.findIndex((i) => i.ai === identifier.ai)
    )

    // Sort identifiers to maintain order (GTIN first, then others) - same as main logic
    uniqueIdentifiers.sort((a, b) => {
      if (a.ai === '01') return -1
      if (b.ai === '01') return 1
      return 0
    })

    // Construct the combined barcode string with parentheses
    return uniqueIdentifiers.map(({ ai, value }) => `(${ai})${value}`).join('')
  }
}
